import { useEffect, useState, useCallback } from 'react'
import { IconButton, Spinner, Chip } from '@material-tailwind/react'
import { FiRefreshCw } from 'react-icons/fi'
import { visitServices } from '@/services/visit-services'
import { Pagination } from '@/shared/components/pagination'
import { UsageLogModal } from '../components/usage-log-modal'
import { FiltersBar } from './filters-bar'
import { HistoryCard } from './history-card'
import { fmt } from './utils'

export function VisitHistory() {
  const [rows, setRows] = useState([])
  const [usageOpen, setUsageOpen] = useState(false)
  const [usageTarget, setUsageTarget] = useState(null)
  const [meta, setMeta] = useState({ page: 1, pages: 1, count: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [stateFilter, setStateFilter] = useState('all') // all | expired | rejected
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const perPage = 10

  const fetchData = useCallback(async () => {
    setError(''); setLoading(true)
    try {
      const params = {
        page: meta.page || 1,
        per_page: perPage,
        q: q || undefined,
        final_state: stateFilter !== 'all' ? stateFilter : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }
      const { items, meta: m } = await visitServices.listHistory(params)
      setRows(Array.isArray(items) ? items : [])
      setMeta(m || { page: 1, pages: 1, count: items?.length || 0 })
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Error al cargar historial')
    } finally {
      setLoading(false)
    }
  }, [meta.page, q, stateFilter, dateFrom, dateTo])

  useEffect(() => { fetchData() }, [fetchData])

  const onPageChange = ({ selected }) => {
    // Pagination usa base 0; Pagy usa base 1
    setMeta((prev) => ({ ...prev, page: (selected ?? 0) + 1 }))
  }

  const openUsageModal = (inv) => {
    const usage = inv?.usage_data
    if (!usage || !Array.isArray(usage.events) || usage.events.length === 0) return
    const vd = inv?.visitor_data || {}
    setUsageTarget({
      usage,
      title: `Uso de invitación — ${vd.name || 'Invitado'}`,
      visitor: vd
    })
    setUsageOpen(true)
  }

  const closeUsageModal = () => {
    setUsageOpen(false)
    setUsageTarget(null)
  }

  return (
    <div className="space-y-3 w-full">
      {/* Encabezado compacto con contador y botón de refresco */}
      <div className="flex items-center gap-3">
        <IconButton variant="text" onClick={fetchData} title="Actualizar">
          {loading ? <Spinner className="h-4 w-4" /> : <FiRefreshCw />}
        </IconButton>
        <Chip value={`${meta.count || 0} total`} size="sm" className="rounded-full" color="blue-gray" />
      </div>

      {/* Barra de filtros */}
      <FiltersBar
        q={q}
        setQ={setQ}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
      />

      {/* Estados */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-600">
          <Spinner className="h-4 w-4" /> Cargando…
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">{error}</div>
      ) : rows.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg p-3">
          No hay registros para los filtros aplicados.
        </div>
      ) : (
        <div className="space-y-3 w-full">
          {rows.map(inv => (
            <HistoryCard key={inv.id} inv={inv} onOpenUsage={openUsageModal} fmt={fmt} />
          ))}
        </div>
      )}

      {/* Paginación */}
      <div className="flex justify-center items-end mt-4">
        <Pagination
          onPageChange={onPageChange}
          page={(meta.page || 1) - 1}
          totalPage={meta.pages || 1}
        />
      </div>

      {/* Modal de registro de usos */}
      <UsageLogModal
        open={usageOpen}
        onClose={closeUsageModal}
        usage={usageTarget?.usage}
        title={usageTarget?.title}
        visitor={usageTarget?.visitor}
      />
    </div>
  )
}

export default VisitHistory